import React, {useContext, useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useRecoilState, useSetRecoilState} from "recoil";
import {AxiosError} from "axios";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {isMobile} from "react-device-detect";
import VehicleStatus from "./vehicle-status/VehicleStatus";
import CheckDamage from "./check-damage/CheckDamage";
import CheckEquipment from "./check-equipment/CheckEquipment";
import Summary from "./summary/Summary";
import Signature from "./signature/Signature";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../util/component/CustomLoader";
import {checkSubstitutionCheckIn, fetchRaCheckOutIn, fetchSearchRaByRaIdCode} from "./services/http.services";
import {checkOutInModeState, checkOutInState, checkOutInStepState, substitutionCheckInState, raFetchedDataState} from "./state";
import RentableAgain from "./rentable-again/RentableAgain";
import {getCheckOutInSessionStorage, saveCheckOutInSessionStorage} from "./services/sessionStorageManager";
import NotAllowedDialog from "../../authentication/component/NotAllowedDialog";

const CheckIn = () => {
    const {t} = useTranslation(['network', 'translation']);
    const navigate = useNavigate();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const params = useParams();

    const [checkOutIn, setCheckOutIn] = useRecoilState(checkOutInState);
    const [checkStep, setCheckStep] = useRecoilState(checkOutInStepState);
    const [checkOutInMode, setCheckOutInMode] = useRecoilState(checkOutInModeState);
    const setSubstitutionCheckIn = useSetRecoilState(substitutionCheckInState);
    const [raFetchedData, setRaFetchedData] = useRecoilState(raFetchedDataState);
    /// Any Unconfirmed Customer Reports Value -> either hasUnconfirmedCustomerReports or hasUnconfirmedCustomerReportsCheckIn are true
    const [hasAnyUnconfirmedCustomerReportsValue, setHasAnyUnconfirmedCustomerReportsValue] = useState<boolean>(false);

    const viewErrorMessage = (message: string) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    /** Confirm Form Mutation */
    const checkSubstitutionCheckInMutation = useMutation(
        checkSubstitutionCheckIn,
        {
            onSuccess: async (response: any) => {
                if (response.substitution) {
                    setCheckOutInMode('subIn');
                    setSubstitutionCheckIn({nonRunningVehicle: response.nonRunningVehicle, reasonCode: response.reasonCode, eventDate: response.eventDate});
                } else {
                    setCheckOutInMode('in');
                }
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    /** Confirm Form Mutation */
    const fetchRaCheckOutInMutation = useMutation(
        fetchRaCheckOutIn,
        {
            onSuccess: async (response: any) => {
                if (!!response) {
                    setRaFetchedData(response);
                    //console.log('fetchRaCheckOutInMutation response: ' + JSON.stringify(response))
                    checkUnconfirmedReportMessage(response.hasUnconfirmedCustomerReports || response.hasUnconfirmedCustomerReportsCheckIn);
                }
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

        const checkUnconfirmedReportMessage = (hasAnyUnconfirmedCustomerReports : Boolean) =>{
            //let hasUnconfirmedCustomerReports: Boolean = getHasUnconfirmedCustomerReportsValue();
            //console.log('checkUnconfirmedReportMessage hasUnconfirmedCustomerReports: ' + hasUnconfirmedCustomerReports);

            if(hasAnyUnconfirmedCustomerReports === true){
                setHasAnyUnconfirmedCustomerReportsValue(true);
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('raCheckOutIn.error.unconfirmedReportCheckIn'),
                            type: snackbarTypes.error,
                            show: true,
                            //autoClose: false,
                        }
                    }
                });
            }else{
                setHasAnyUnconfirmedCustomerReportsValue(false);
            }
        }

    const fetchSearchRaByRaIdCodeQuery = useQuery(
        ['fetchSearchRaByRaIdCode', {id: params.id}],
        () => fetchSearchRaByRaIdCode({id: params.id, type: 'check-in', recoveryMode: false}),
        {
            enabled: false,
            onSuccess: (response: any) => {

                if (response.executed) {
                    viewErrorMessage('raCheckOutIn.raVehicleIdentification.message.error.executed');
                    navigate('/');
                } else {
                    checkSessionData();
                }
            },
            onError: (error: AxiosError) => {
                let mess = '';
                // @ts-ignore
                if (error.response?.status === 404 && error.response?.data?.errorDetails === "\"ERROR_ID_RA_NOT_FOUND\"") {
                    mess = "common.error.notExistingRa"
                }
                // @ts-ignore
                else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\""){
                    mess = "common.error.errorInconsistentRaState"
                }
                // @ts-ignore
                else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_RA_TO_RECONCILE\""){
                    mess = "common.error.errorRaToReconcile"
                }
                else{
                    mess = 'translation:error.tryLater';
                }
                viewErrorMessage(mess);
            }
        })

    useEffect(() => {
        if (checkStep.active !== null) {
            saveCheckOutInSessionStorage(checkOutIn);
        }
    }, [checkStep])

    const checkSessionData = async () => {
        let sessionStrData: any = getCheckOutInSessionStorage();
        if (!!sessionStrData && params.id === sessionStrData['raFetchedData'].idRentalAgreement && sessionStrData.type === 'in') {
            await setCheckOutIn(sessionStrData);
            checkUnconfirmedReportMessage(sessionStrData['raFetchedData'].hasUnconfirmedCustomerReports || sessionStrData['raFetchedData'].hasUnconfirmedCustomerReportsCheckIn);
        }
        else {
            fetchRaCheckOutInMutation.mutate({id: params.id} as any);
        }

        checkSubstitutionCheckInMutation.mutate({idRentalAgreement: params.id} as any);
    }

    useEffect(() => {
        if (!!checkOutInMode && !!raFetchedData) {
            setCheckStep({
                active: 0,// goto step 0 for reinsert img
                numSteps: 6
            });
        }
    }, [checkOutInMode, raFetchedData]);

    useEffect(() => {
        if (isMobile) {
            fetchSearchRaByRaIdCodeQuery.refetch();
        }
    }, [])

    return (
        <Box>
            <SectionHeader title={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t("raCheckOutIn.in.header.title")}</Typography>
                    </Stack>
                </Stack>
            } />
            {
            	!isMobile ?
            		<NotAllowedDialog title={t("raCheckOutIn.label.operationNotAllowed")} message={t("raCheckOutIn.label.switchToMobile")}/>:
                    <ContentBody>
                        {/* Hide if there are customerReport still pending */}
                        {//hasUnconfirmedCustomerReportsValue === false && (
							<>
                            { checkStep.active === 0 && <VehicleStatus  disableContinueButton={hasAnyUnconfirmedCustomerReportsValue}/> }
                            { checkStep.active === 1 && <CheckDamage /> }
                            { checkStep.active === 2 && <CheckEquipment /> }
                            { checkStep.active === 3 && <Summary /> }
                            { checkStep.active === 4 && <Signature /> }
                            { checkStep.active === 5 && <RentableAgain /> }
                        </>
					//)
                    }
                    </ContentBody>
            }

            <CustomLoader loading={checkSubstitutionCheckInMutation.isLoading || fetchRaCheckOutInMutation.isLoading} />
        </Box>
    )
}

export default CheckIn;
